import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { subscriptionService, authService } from '../services/api';
import { Plan, User } from '../types';
import CancelSubscription from '../components/CancelSubscription';
import InvoiceHistory from '../components/InvoiceHistory';

interface PlanOption {
  id: string;
  name: string;
  description?: string;
  price: number;
  requestsPerMonth: number;
  requestsPerMinute: number;
  maxClients: number;
  bandwidth: number;
  features: string[];
}

// Utilisation du type Plan importé depuis '../types'

const Subscription: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(user?.currency || 'CHF');

  useEffect(() => {
    // Charger les plans disponibles
    const loadPlans = async () => {
      try {
        const availablePlans = await subscriptionService.getPlans();
        // Convertir les plans de l'API au format attendu par le composant
        const formattedPlans: PlanOption[] = availablePlans.map((plan: Plan) => ({
          id: plan.id,
          name: plan.name,
          description: plan.description,
          price: plan.price,
          requestsPerMonth: plan.requestsPerMonth,
          requestsPerMinute: plan.requestsPerMinute,
          maxClients: plan.maxClients,
          bandwidth: plan.bandwidth || 1, // Valeur par défaut de 1 Go si non défini
          features: plan.features
        }));
        setPlans(formattedPlans);
        
        // Sélectionner le plan actuel de l'utilisateur
        if (user?.plan?.id) {
          setSelectedPlan(user.plan.id);
        }
      } catch (err) {
        setError('Erreur lors du chargement des plans');
      } finally {
        setIsLoading(false);
      }
    };

    loadPlans();
  }, [user]);

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
  };

  // Déterminer si le changement est une mise à niveau ou une rétrogradation
  const isPlanUpgrade = (newPlanId: string): boolean => {
    if (!user?.plan?.id) return true; // Si pas de plan actuel, considérer comme une mise à niveau
    
    const currentPlan = plans.find(p => p.id === user.plan?.id);
    const newPlan = plans.find(p => p.id === newPlanId);
    
    if (!currentPlan || !newPlan) return true;
    
    // Comparer les prix pour déterminer si c'est une mise à niveau
    return newPlan.price >= currentPlan.price;
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const selectedPlanDetails = plans.find(p => p.id === selectedPlan);
      const isUpgrade = isPlanUpgrade(selectedPlan);
      
      // Déterminer quelle devise utiliser
      // Si l'utilisateur a déjà un abonnement payant, utiliser sa devise actuelle
      // Sinon, utiliser la devise sélectionnée
      const currencyToUse = (user?.plan && user?.plan?.price > 0) 
        ? (user?.currency || 'CHF') 
        : selectedCurrency;
      
      // Si le plan est gratuit, on fait une mise à jour immédiate
      if (selectedPlanDetails?.price === 0) {
        const response = await subscriptionService.changePlan(selectedPlan, currencyToUse);
        
        // Vérifier si la réponse est un utilisateur valide avec les propriétés requises
        if (response && typeof response === 'object' && 'id' in response && 'email' in response) {
          const updatedUser = response as unknown as User;
          if (updateUser) {
            updateUser(updatedUser);
          }
        }
        
        setSuccess(`Votre abonnement a été mis à jour avec succès au plan ${selectedPlanDetails?.name}`);
      } else {
        // Pour les plans payants, rediriger vers Stripe Checkout
        const response = await subscriptionService.changePlan(selectedPlan, currencyToUse);
        
        // Stocker l'information sur le type de changement pour l'afficher après redirection
        localStorage.setItem('subscription_change_type', isUpgrade ? 'upgrade' : 'downgrade');
        
        // Si la réponse contient une URL de session, rediriger vers Stripe
        if (response && typeof response === 'object' && 'sessionUrl' in response) {
          window.location.href = response.sessionUrl as string;
        } 
        // Si la réponse indique un succès sans URL de session (mise à jour automatique)
        else if (response && typeof response === 'object' && 'success' in response && response.success) {
          setSuccess(response.message || `Votre abonnement est en cours de mise à jour au plan ${selectedPlanDetails?.name}`);
          
          // Rafraîchir les informations utilisateur après un court délai
          setTimeout(async () => {
            try {
              // Utiliser le service d'authentification pour récupérer les informations utilisateur mises à jour
              const userData = await authService.getCurrentUser();
              if (updateUser && userData) {
                updateUser(userData);
              }
            } catch (error) {
              console.error('Erreur lors de la récupération des informations utilisateur', error);
            }
          }, 2000);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du changement d\'abonnement');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Gestion de l'abonnement</h1>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Gestion de l'abonnement</h1>
      
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700 font-medium">Politique de changement d'abonnement</p>
            <ul className="mt-2 text-sm text-blue-700 list-disc list-inside">
              <li>Passage à un plan supérieur : application immédiate avec facturation au prorata pour le mois en cours</li>
              <li>Passage à un plan inférieur : application au début du prochain cycle de facturation</li>
            </ul>
          </div>
        </div>

      </div>
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">Votre plan actuel</h2>
        <Card>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h3 className="text-lg font-medium">{user?.plan?.name || 'Plan gratuit'}</h3>
              <p className="text-gray-600">{user?.plan?.price ? `${user.plan.price}€/mois` : 'Gratuit'}</p>
            </div>
            <div className="mt-4 md:mt-0">
              <p className="text-gray-600">
                <span className="font-semibold">{user?.plan?.maxClients || 1}</span> clients autorisés
              </p>
              <p className="text-gray-600">
                <span className="font-semibold">{user?.plan?.requestsPerMonth?.toLocaleString() || 1000}</span> requêtes/mois
              </p>
            </div>
          </div>
          
          {/* Afficher le bouton d'annulation d'abonnement si l'utilisateur a un plan payant */}
          {user?.plan && user.plan.price && user.plan.price > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <CancelSubscription />
            </div>
          )}
        </Card>
      </div>
      
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Choisir un plan</h2>
        
        {/* Sélecteur de devise - visible uniquement si l'utilisateur est sur un plan gratuit ou n'a pas d'abonnement */}
        {(!user?.plan || user?.plan?.price === 0) && (
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Devise:</span>
            <div className="flex items-center space-x-4 bg-gray-50 p-2 rounded-md">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="currency"
                  value="CHF"
                  checked={selectedCurrency === 'CHF'}
                  onChange={() => setSelectedCurrency('CHF')}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2">CHF</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="currency"
                  value="EUR"
                  checked={selectedCurrency === 'EUR'}
                  onChange={() => setSelectedCurrency('EUR')}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2">EUR</span>
              </label>
            </div>
          </div>
        )}
        
        {/* Message d'information sur la devise pour les utilisateurs avec un abonnement payant */}
        {user?.plan && user?.plan?.price > 0 && (
          <div className="text-sm text-gray-600">
            Devise actuelle: <span className="font-medium">{user?.currency || 'CHF'}</span>
            <span className="ml-1 text-xs">(ne peut pas être modifiée pour un abonnement existant)</span>
          </div>
        )}
      </div>
      
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700 font-medium">Information sur les prix</p>
            <p className="mt-1 text-sm text-blue-700">
              Tous les prix affichés sont toutes taxes comprises (TTC). Le montant exact indiqué est celui que vous paierez, quelle que soit la TVA applicable dans votre pays.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {plans.map((plan) => (
          plan.id === 'enterprise' ? null : (
          <Card 
            key={plan.id} 
            className={`cursor-pointer transition-all ${selectedPlan === plan.id ? 'border-blue-500 border-2 shadow-lg' : 'hover:shadow-md'}`}
            onClick={() => handlePlanSelect(plan.id)}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-medium">{plan.name}</h3>
              <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                {plan.price + ' ' + selectedCurrency + '/mois'}
                <span className="text-xs block text-right">TTC</span>
              </div>  
            </div>
            
            <p className="text-gray-600 mb-4">{plan.description}</p>
            
            <ul className="space-y-2 mb-6">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </Card>
        )))}
      </div>
      
      <div className="flex justify-center">
        <Button
          onClick={handleSubscribe}
          disabled={!selectedPlan || isSubmitting || (user?.plan?.id === selectedPlan)}
          className="w-full md:w-auto"
          variant="primary"
          isLoading={isSubmitting}
        >
          {isSubmitting ? 'Traitement en cours...' : 'Mettre à jour l\'abonnement'}
        </Button>
      </div>
      
      {/* Historique des factures */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">Historique des factures</h2>
        <InvoiceHistory />
      </div>
    </div>
  );
};

export default Subscription;
